import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthProvider";
import Monogram from "../components/Monogram";

function formatErr(detail) {
  if (!detail) return "An error occurred.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await login(email, password);
      toast.success("Welcome back.");
      const dest = loc.state?.from || "/admin/leads";
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = formatErr(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#050505] grid lg:grid-cols-2" data-testid="page-admin-login">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="/generated/founder_director_monitor.jpg" alt="" className="absolute inset-0 w-full h-full object-cover saturate-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 z-10">
          <div className="font-serif-editorial text-5xl text-ink-100 leading-[1.02] max-w-md">Backstage of the studio.</div>
          <div className="mt-4 text-ink-300 text-sm max-w-md">Manage leads, generate visuals, edit the journal.</div>
        </div>
      </div>
      <div className="flex flex-col justify-center px-8 lg:px-20 py-20">
        <Monogram size={42} withWord />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-16 max-w-md">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-ink-300">
            <span className="block w-8 h-px bg-rouge" /><span>Admin</span>
          </div>
          <h1 className="mt-6 font-serif-editorial text-5xl text-ink-100 leading-[1.02]">Welcome back.</h1>
          <p className="mt-4 text-ink-300 text-sm">Sign in to access the studio dashboard.</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-6" data-testid="admin-login-form">
            <div>
              <label className="text-[10px] tracking-[0.24em] uppercase text-ink-500 mb-2 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="editorial-field"
                data-testid="admin-email"
                placeholder="admin@phanerooventures.com"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.24em] uppercase text-ink-500 mb-2 block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="editorial-field"
                data-testid="admin-password"
                placeholder="••••••••••"
              />
            </div>
            {error && <div className="text-rouge text-sm" data-testid="admin-error">{error}</div>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-submit"
              className="group inline-flex items-center gap-3 bg-rouge hover:bg-rouge-deep disabled:opacity-60 text-ink-100 px-8 py-4 text-[11px] tracking-[0.24em] uppercase transition-colors"
            >
              <Lock size={14} /> {loading ? "Signing in…" : "Sign in"}
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </motion.div>
      </div>
    </main>
  );
}
